import React, { useEffect, useState } from 'react';

import { transportStatementsAPI } from '../../lib/inboundRentalsApi';
import { messageErreur } from '../../lib/erreurs';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../ui/sonner';

/**
 * Réglages de la société pour les états de paiement du transport (administrateur):
 * son sigle (le « PKS » de « BPS F005-PKS ») et le premier numéro de ligne de
 * facture (131, 132…), qui ne change plus une fois la série entamée.
 */
const ReglagesEtatsDialog = ({ open, onOpenChange, onSaved }) => {
  const [reglages, setReglages] = useState(null);
  const [sigle, setSigle] = useState('');
  const [depart, setDepart] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let annule = false;
    setReglages(null);
    transportStatementsAPI
      .settings()
      .then((donnees) => {
        if (annule) return;
        setReglages(donnees);
        setSigle(donnees?.company_code || '');
        setDepart(String(donnees?.invoice_line_next ?? 1));
      })
      .catch((error) => {
        if (annule) return;
        toast.error(messageErreur(error, 'Impossible de charger les réglages'));
        onOpenChange(false);
      });
    return () => {
      annule = true;
    };
  }, [open, onOpenChange]);

  const verrouille = Boolean(reglages?.invoice_line_locked);
  const nombre = Number(depart);
  const departValide = verrouille || (Number.isInteger(nombre) && nombre >= 1);
  const sigleValide = /^[A-Z0-9_-]{0,20}$/.test(sigle);

  const enregistrer = async () => {
    setBusy(true);
    try {
      const corps = { company_code: sigle.trim().toUpperCase() };
      if (!verrouille && nombre !== Number(reglages?.invoice_line_next)) corps.invoice_line_start = nombre;
      await transportStatementsAPI.updateSettings(corps);
      toast.success('Réglages enregistrés');
      onOpenChange(false);
      if (onSaved) await onSaved();
    } catch (error) {
      toast.error(messageErreur(error, 'Enregistrement des réglages impossible'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Réglages des états de paiement</DialogTitle>
          <DialogDescription className="sr-only">Sigle de la société et premier numéro de facture</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="reglage-sigle-societe">Sigle de la société</Label>
            <Input
              id="reglage-sigle-societe"
              value={sigle}
              maxLength={20}
              placeholder="PKS"
              disabled={!reglages}
              onChange={(event) => setSigle(event.target.value.toUpperCase())}
              className="w-40 uppercase"
              data-testid="reglage-sigle-societe"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="reglage-premiere-facture">Premier numéro de facture</Label>
            <Input
              id="reglage-premiere-facture"
              type="number"
              min="1"
              step="1"
              inputMode="numeric"
              value={depart}
              disabled={!reglages || verrouille}
              onChange={(event) => setDepart(event.target.value)}
              className="w-40 text-right tabular-nums"
              data-testid="reglage-premiere-facture"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            onClick={enregistrer}
            disabled={busy || !reglages || !sigleValide || !departValide}
            data-testid="enregistrer-reglages-etats"
          >
            {busy ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReglagesEtatsDialog;
